import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

export const ContactStaggeredMenu = ({
  position = 'right',
  colors = ['#0c6749ff', '#073A29'],
  items = [],
  socialItems = [],
  displaySocials = true,
  displayItemNumbering = true,
  accentColor = '#073A29',
  onMenuOpen,
  onMenuClose
}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const serviceOptions = [
    'Custom Software Development',
    'Web Development',
    'Mobile App Development',
    'UI/UX Design',
    'Cloud Solutions',
    'AI/ML Solutions',
    'Other',
  ];

  const panelRef = useRef(null);
  const preLayersRef = useRef(null);
  const preLayerElsRef = useRef([]);

  const openTlRef = useRef(null);
  const closeTweenRef = useRef(null);
  const busyRef = useRef(false);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const panel = panelRef.current;
      const preContainer = preLayersRef.current;

      if (!panel || !preContainer) return;

      let preLayers = [];
      if (preContainer) {
        preLayers = Array.from(preContainer.querySelectorAll('.sm-prelayer'));
      }
      preLayerElsRef.current = preLayers;

      const offscreen = 100;
      gsap.set([panel, ...preLayers], { xPercent: offscreen });
    });
    return () => ctx.revert();
  }, []);

  const buildOpenTimeline = useCallback(() => {
    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return null;

    openTlRef.current?.kill();
    if (closeTweenRef.current) {
      closeTweenRef.current.kill();
      closeTweenRef.current = null;
    }

    const itemEls = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
    const numberEls = Array.from(panel.querySelectorAll('.sm-panel-list[data-numbering] .sm-panel-item'));
    const socialTitle = panel.querySelector('.sm-socials-title');
    const socialLinks = Array.from(panel.querySelectorAll('.sm-socials-link'));
    const formElements = Array.from(panel.querySelectorAll('.form-element'));
    const menuTitle = panel.querySelector('.menu-title');

    const layerStates = layers.map(el => ({ el, start: Number(gsap.getProperty(el, 'xPercent')) }));
    const panelStart = Number(gsap.getProperty(panel, 'xPercent'));

    if (itemEls.length) gsap.set(itemEls, { yPercent: 140, rotate: 10 });
    // eslint-disable-next-line no-useless-computed-key
    if (numberEls.length) gsap.set(numberEls, { ['--sm-num-opacity']: 0 });
    if (socialTitle) gsap.set(socialTitle, { opacity: 0 });
    if (socialLinks.length) gsap.set(socialLinks, { y: 25, opacity: 0 });
    if (formElements.length) gsap.set(formElements, { y: 30, opacity: 0 });
    if (menuTitle) gsap.set(menuTitle, { y: 20, opacity: 0 });

    const tl = gsap.timeline({ paused: true });

    layerStates.forEach((ls, i) => {
      tl.fromTo(ls.el, { xPercent: ls.start }, { xPercent: 0, duration: 0.5, ease: 'power4.out' }, i * 0.07);
    });

    const lastTime = layerStates.length ? (layerStates.length - 1) * 0.07 : 0;
    const panelInsertTime = lastTime + (layerStates.length ? 0.08 : 0);
    const panelDuration = 0.65;

    tl.fromTo(
      panel,
      { xPercent: panelStart },
      { xPercent: 0, duration: panelDuration, ease: 'power4.out' },
      panelInsertTime
    );

    if (menuTitle) {
      tl.to(menuTitle, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }, panelInsertTime + 0.2);
    }

    if (itemEls.length) {
      const itemsStartRatio = 0.15;
      const itemsStart = panelInsertTime + panelDuration * itemsStartRatio;

      tl.to(
        itemEls,
        { yPercent: 0, rotate: 0, duration: 1, ease: 'power4.out', stagger: { each: 0.1, from: 'start' } },
        itemsStart
      );

      if (numberEls.length) {
        // eslint-disable-next-line no-useless-computed-key
        tl.to(
          numberEls,
          // eslint-disable-next-line no-useless-computed-key
          { duration: 0.6, ease: 'power2.out', ['--sm-num-opacity']: 1, stagger: { each: 0.08, from: 'start' } },
          itemsStart + 0.1
        );
      }
    }

    if (socialTitle || socialLinks.length) {
      const socialsStart = panelInsertTime + panelDuration * 0.4;

      if (socialTitle) tl.to(socialTitle, { opacity: 1, duration: 0.5, ease: 'power2.out' }, socialsStart);
      if (socialLinks.length) {
        tl.to(
          socialLinks,
          {
            y: 0,
            opacity: 1,
            duration: 0.55,
            ease: 'power3.out',
            stagger: { each: 0.08, from: 'start' },
            onComplete: () => gsap.set(socialLinks, { clearProps: 'opacity' })
          },
          socialsStart + 0.04
        );
      }
    }

    if (formElements.length) {
      const formsStart = panelInsertTime + panelDuration * 0.3;
      tl.to(
        formElements,
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power3.out',
          stagger: { each: 0.08, from: 'start' },
          overwrite: true
        },
        formsStart
      );
    }

    openTlRef.current = tl;
    return tl;
  }, []);

  const playOpen = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    setOpen(true);
    onMenuOpen?.();

    const tl = buildOpenTimeline();
    if (tl) {
      tl.eventCallback('onComplete', () => {
        busyRef.current = false;
      });
      tl.play(0);
    } else {
      busyRef.current = false;
    }
  }, [buildOpenTimeline, onMenuOpen]);

  const playClose = useCallback(() => {
    openTlRef.current?.kill();
    openTlRef.current = null;

    const panel = panelRef.current;
    const layers = preLayerElsRef.current;
    if (!panel) return;

    const all = [...layers, panel];
    closeTweenRef.current?.kill();

    const offscreen = 100;

    closeTweenRef.current = gsap.to(all, {
      xPercent: offscreen,
      duration: 0.32,
      ease: 'power3.in',
      overwrite: 'auto',
      onComplete: () => {
        const itemEls = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
        if (itemEls.length) gsap.set(itemEls, { yPercent: 140, rotate: 10 });

        const numberEls = Array.from(panel.querySelectorAll('.sm-panel-list[data-numbering] .sm-panel-item'));
        // eslint-disable-next-line no-useless-computed-key
        if (numberEls.length) gsap.set(numberEls, { ['--sm-num-opacity']: 0 });

        const socialTitle = panel.querySelector('.sm-socials-title');
        const socialLinks = Array.from(panel.querySelectorAll('.sm-socials-link'));
        if (socialTitle) gsap.set(socialTitle, { opacity: 0 });
        if (socialLinks.length) gsap.set(socialLinks, { y: 25, opacity: 0 });

        const formElements = Array.from(panel.querySelectorAll('.form-element'));
        if (formElements.length) gsap.set(formElements, { translateY: 30, opacity: 0 });

        setOpen(false);
        busyRef.current = false;
        onMenuClose?.();
      }
    });
  }, [onMenuClose]);

  // Auto-open when component mounts
  React.useEffect(() => {
    const timer = setTimeout(() => {
      playOpen();
    }, 50);
    return () => clearTimeout(timer);
  }, [playOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('company', formData.company);
    formDataToSend.append('service', formData.service);
    formDataToSend.append('message', formData.message);

    fetch('https://formspree.io/f/myzlgorb', {
      method: 'POST',
      body: formDataToSend,
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => {
      if (response.ok) {
        console.log('✅ Email sent successfully');
        setSubmitted(true);
        setTimeout(() => {
          setFormData({ name: '', email: '', company: '', service: '', message: '' });
          setSubmitted(false);
        }, 3000);
      } else {
        console.error('❌ Failed to send email');
        setError('Failed to send message. Please try again.');
      }
    })
    .catch((error) => {
      console.error('❌ Error sending email:', error);
      setError('Failed to send message. Please try again.');
    });
  };

  return (
    <div className="sm-scope z-40 fixed top-0 left-0 w-screen h-screen overflow-hidden">
      <div
        className="staggered-menu-wrapper relative w-full h-full"
        // eslint-disable-next-line no-useless-computed-key
        style={accentColor ? { ['--sm-accent']: accentColor } : undefined}
        data-position={position}
        data-open={open || undefined}
      >
        <div
          ref={preLayersRef}
          className="sm-prelayers absolute top-0 right-0 bottom-0 pointer-events-none z-[5]"
          aria-hidden="true"
        >
          {colors.map((c, i) => (
            <div
              key={i}
              className="sm-prelayer absolute top-0 right-0 h-full w-full translate-x-0"
              style={{ background: c, width: 'clamp(300px, 40vw, 500px)' }}
            />
          ))}
        </div>

        <aside
          id="staggered-menu-panel"
          ref={panelRef}
          className="staggered-menu-panel absolute top-0 right-0 h-full bg-white flex flex-col p-2 overflow-y-auto z-10 backdrop-blur-[12px]"
          style={{
            WebkitBackdropFilter: 'blur(12px)',
            width: 'clamp(300px, 40vw, 500px)'
          }}
          aria-hidden={!open}
        >
          {/* Close Button */}
          <button
            onClick={playClose}
            className="absolute top-4 right-4 flex items-center gap-2 px-4 py-3 text-black hover:text-gray-600 transition-colors duration-200 z-20 bg-transparent border-none cursor-pointer"
            aria-label="Close menu"
          >
            <span className="text-base font-medium">Close</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="sm-panel-inner flex-1 flex flex-col gap-3 mt-0">
            {/* Contact Form Title Section */}
            <div className="menu-title">
              <p className="text-[#073A29] text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 bg-[#073A29] rounded-full"></span>
                GET IN TOUCH
              </p>
              <h2 className="text-4xl lg:text-5xl font-bold text-black mb-2">
                Let's Start Your Project
              </h2>
              <p className="text-gray-600 text-base">
                Have a project in mind? Send us a message and our team will get back to you within 24 hours.
              </p>
            </div>

            {displaySocials && (
              <div className="flex flex-col gap-6">
                {/* Contact Form */}
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-12 form-element">
                    {/* Success Icon with Animation */}
                    <div className="relative mb-6">
                      {/* Expanding background circle */}
                      <div className="absolute bg-[#073A29]/10 rounded-full animate-expand-circle" style={{ width: '80px', height: '80px', left: '-8px', top: '-8px' }} />
                      
                      {/* Main icon circle */}
                      <div className="relative w-16 h-16 bg-[#073A29] rounded-full flex items-center justify-center shadow-lg animate-scale-in">
                        {/* Checkmark */}
                        <svg className="w-8 h-8 text-white animate-checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold text-black mb-2 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                      Message Sent
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-center text-sm max-w-xs animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                    {error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-xs">
                        {error}
                      </div>
                    )}
                    {/* Name and Email */}
                    <div className="form-element">
                      <label htmlFor="name" className="block text-sm font-semibold text-black mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm !bg-white"
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="form-element">
                      <label htmlFor="email" className="block text-sm font-semibold text-black mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm !bg-white"
                        placeholder="john@example.com"
                      />
                    </div>

                    {/* Company and Service */}
                    <div className="form-element">
                      <label htmlFor="company" className="block text-sm font-semibold text-black mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm !bg-white"
                        placeholder="Your Company"
                      />
                    </div>

                    <div className="form-element">
                      <label htmlFor="service" className="block text-sm font-semibold text-black mb-2">
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm appearance-none cursor-pointer !bg-white"
                      >
                        <option value="">Select a service</option>
                        {serviceOptions.map((service, index) => (
                          <option key={index} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Message Field */}
                    <div className="form-element">
                      <label htmlFor="message" className="block text-sm font-semibold text-black mb-2">
                        Tell us about your project *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="1"
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#EDEDED] focus:border-[#073A29] focus:outline-none transition-colors duration-200 text-sm resize-none !bg-white"
                        placeholder="Describe your project..."
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className="w-full bg-black text-white py-4 rounded-xl font-semibold hover:bg-[#073A29] transition-colors duration-200 text-sm form-element flex items-center justify-center gap-2 group"
                    >
                      <span>Send Message</span>
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Socials Section */}
            {displaySocials && socialItems && socialItems.length > 0 && (
              <div className="sm-socials mt-0 pt-0 flex flex-col gap-3" aria-label="Social links">
                <h3 className="sm-socials-title m-0 text-base font-medium [color:var(--sm-accent,#ff0000)]">Socials</h3>
                <ul
                  className="sm-socials-list list-none m-0 p-0 flex flex-row items-center gap-4 flex-wrap"
                >
                  {socialItems.map((s, i) => (
                    <li key={s.label + i} className="sm-socials-item">
                      <a
                        href={s.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sm-socials-link text-[1.2rem] font-medium text-[#111] no-underline relative inline-block py-[2px] transition-[color,opacity] duration-300 ease-linear"
                      >
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>

      <style>{`
        .sm-scope .staggered-menu-wrapper { position: relative; width: 100%; height: 100%; z-index: 40; }
        .sm-scope .sm-prelayers { position: absolute; top: 0; right: 0; bottom: 0; pointer-events: none; z-index: 5; }
        .sm-scope .sm-prelayer { position: absolute; top: 0; right: 0; height: 100%; width: 100%; transform: translateX(0); }
        .sm-scope .staggered-menu-panel { position: absolute; top: 0; right: 0; height: 100%; background: #ffffff !important; backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); display: flex; flex-direction: column; padding: 6em 2em 2em 2em; overflow-y: auto; z-index: 10; }
        .sm-scope .sm-panel-inner { flex: 1; display: flex; flex-direction: column; gap: 1.25rem; }
        .sm-scope .sm-socials { margin-top: 0; padding-top: 0.5rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .sm-scope .sm-socials-title { margin: 0; font-size: 1rem; font-weight: 500; color: var(--sm-accent, #ff0000); }
        .sm-scope .sm-socials-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: row; align-items: center; gap: 1rem; flex-wrap: wrap; }
        .sm-scope .sm-socials-list .sm-socials-link { opacity: 1; transition: opacity 0.3s ease; }
        .sm-scope .sm-socials-list:hover .sm-socials-link:not(:hover) { opacity: 0.35; }
        .sm-scope .sm-socials-list:focus-within .sm-socials-link:not(:focus-visible) { opacity: 0.35; }
        .sm-scope .sm-socials-list .sm-socials-link:hover,
        .sm-scope .sm-socials-list .sm-socials-link:focus-visible { opacity: 1; }
        .sm-scope .sm-socials-link:focus-visible { outline: 2px solid var(--sm-accent, #ff0000); outline-offset: 3px; }
        .sm-scope .sm-socials-link { font-size: 1.2rem; font-weight: 500; color: #111; text-decoration: none; position: relative; padding: 2px 0; display: inline-block; transition: color 0.3s ease, opacity 0.3s ease; }
        .sm-scope .sm-socials-link:hover { color: var(--sm-accent, #ff0000); }
        .sm-scope .sm-panel-title { margin: 0; font-size: 1rem; font-weight: 600; color: #fff; text-transform: uppercase; }
        .sm-scope .sm-panel-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
        .sm-scope .sm-panel-item { position: relative; color: #000; font-weight: 600; font-size: 4rem; cursor: pointer; line-height: 1; letter-spacing: -2px; text-transform: uppercase; transition: background 0.25s, color 0.25s; display: inline-block; text-decoration: none; padding-right: 1.4em; }
        .sm-scope .sm-panel-itemLabel { display: inline-block; will-change: transform; transform-origin: 50% 100%; }
        .sm-scope .sm-panel-item:hover { color: var(--sm-accent, #ff0000); }
        .sm-scope .sm-panel-list[data-numbering] { counter-reset: smItem; }
        .sm-scope .sm-panel-list[data-numbering] .sm-panel-item::after { counter-increment: smItem; content: counter(smItem, decimal-leading-zero); position: absolute; top: 0.1em; right: 3.2em; font-size: 18px; font-weight: 400; color: var(--sm-accent, #ff0000); letter-spacing: 0; pointer-events: none; user-select: none; opacity: var(--sm-num-opacity, 0); }
        
        /* Success message animations */
        @keyframes scale-in {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes expand-circle {
          0% { 
            transform: scale(0.8); 
            opacity: 0;
          }
          50% {
            transform: scale(1.15);
            opacity: 0.6;
          }
          100% { 
            transform: scale(1); 
            opacity: 0.3;
          }
        }
        
        @keyframes checkmark {
          0% { stroke-dasharray: 0, 100; stroke-dashoffset: 0; opacity: 0; }
          50% { opacity: 1; }
          100% { stroke-dasharray: 100, 100; stroke-dashoffset: 0; opacity: 1; }
        }
        
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        .animate-scale-in {
          animation: scale-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both;
        }
        
        .animate-expand-circle {
          animation: expand-circle 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
        }
        
        .animate-checkmark {
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: checkmark 0.6s ease-out 0.5s forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.4s ease-out both;
        }
      `}</style>
    </div>
  );
};

export default ContactStaggeredMenu;